"use client";

import { useState } from "react";
import NextImage from "next/image";
import { AnimatePresence, motion } from "framer-motion";

import { cn } from "@/lib/utils";
import { IEventFaqItem, PageEventSchema } from "@/sanity/pages/PageEventTemplate";

import { type EventLayout } from "@/components/pageComponents/event/EventShared";

import iconPlus from "@/public/event/icon-plus.svg";
import iconMinus from "@/public/event/icon-minus.svg";

/** FAQ accordion — one item open at a time, all closed by default. */
function FaqAccordion({ items, initialOpen = -1, layout = "event" }: { items: IEventFaqItem[]; initialOpen?: number; layout?: EventLayout }) {
  const monthly = layout === "monthly";
  const [open, setOpen] = useState(initialOpen);

  return (
    <div className={cn("flex w-full flex-col items-start gap-[1.5rem] lg:w-[85.25rem]", monthly && "max-lg:w-[min(calc(49.75*var(--page-rem)),100%)] max-lg:gap-[calc(1.5*var(--page-rem))]")}>
      {items.map((item, i) => {
        const isOpen = i === open;
        return (
          <button
            key={item._key ?? i}
            type="button"
            aria-expanded={isOpen}
            onClick={() => setOpen(isOpen ? -1 : i)}
            className={cn(
              "flex w-full flex-col items-start text-left transition-[background-color,border-color,padding,border-radius] duration-300 ease-out",
              isOpen
                ? "rounded-[0.5rem] border border-transparent bg-[#f2f2f2] p-[4rem]"
                : "rounded-[0.25rem] border border-black/10 bg-[#969696]/5 p-[3rem]",
              monthly && (isOpen ? "lg:p-[calc(4rem-1px)] max-lg:p-[calc(4*var(--page-rem)-1px)]" : "lg:p-[calc(3rem-1px)] max-lg:p-[calc(3*var(--page-rem)-1px)]"),
            )}>
            <span className={cn("flex w-full items-center gap-[3rem]", monthly && "max-lg:gap-[calc(3*var(--page-rem))]")}>
              <span className={cn("min-w-0 flex-1 font-geist text-[2.25rem] leading-[1.12] font-normal text-black", monthly && "max-lg:text-[calc(2.25*var(--page-rem))]")}>
                {item.question}
              </span>
              <span className="relative h-[2rem] w-[2rem] shrink-0">
                <NextImage
                  src={iconMinus}
                  alt=""
                  aria-hidden
                  className={cn(
                    "absolute inset-0 h-full w-full transition-opacity duration-300",
                    isOpen ? "opacity-100" : "opacity-0",
                  )}
                />
                <NextImage
                  src={iconPlus}
                  alt=""
                  aria-hidden
                  className={cn(
                    "absolute inset-0 h-full w-full transition-[opacity,transform] duration-300",
                    isOpen ? "rotate-90 opacity-0" : "rotate-0 opacity-100",
                  )}
                />
              </span>
            </span>
            <AnimatePresence initial={false}>
              {isOpen && item.answer && (
                <motion.span
                  key="answer"
                  initial={{ height: 0, opacity: 0, marginTop: 0 }}
                  animate={{ height: "auto", opacity: 1, marginTop: "1.25rem" }}
                  exit={{ height: 0, opacity: 0, marginTop: 0 }}
                  transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
                  className={cn("block w-[min(38.125rem,100%)] overflow-hidden font-geist text-[2.25rem] leading-[1.5] font-normal text-black opacity-50 lg:w-[74.25rem]", monthly && "text-black/50 max-lg:w-[min(calc(38.125*var(--page-rem)),100%)] max-lg:text-[calc(2.25*var(--page-rem))]")}>
                  {item.answer}
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        );
      })}
    </div>
  );
}

/** FAQ section: badge + title, accordion. */
export function EventFaq({ data, initialOpen = -1, layout = "event" }: { data: PageEventSchema; initialOpen?: number; layout?: EventLayout }) {
  const monthly = layout === "monthly";
  const faqItems = data.s5Items ?? [];

  return (
    <section className={cn("flex w-full flex-col items-center gap-[4.75rem] bg-white px-[2rem] py-[8rem] lg:py-[15rem]", monthly && "bg-[#fbfbfb] max-lg:gap-[calc(4.75*var(--page-rem))] max-lg:px-[calc(2*var(--page-rem))] max-lg:py-[calc(8*var(--page-rem))]")}>
      <div className={cn("flex flex-col items-center gap-[3.5rem]", monthly && "max-lg:gap-[calc(3.5*var(--page-rem))]")}>
        {data.s5BadgeText && (
          <div className={cn("flex items-center gap-[1.25rem] border border-black/10 bg-black/[0.04] px-[2.5rem] py-[1.5rem]", monthly && "py-0 lg:h-[4.25rem] lg:px-[calc(2.5rem-1px)] max-lg:h-[calc(4.25*var(--page-rem))] max-lg:px-[calc(2.5*var(--page-rem)-1px)] max-lg:gap-[calc(1.25*var(--page-rem))]")}>
            <span className="h-[0.75rem] w-[0.75rem] rounded-full bg-[#1b8869]" />
            <p className={cn("mb-0 font-geist text-[1.75rem] leading-[1.15] font-medium whitespace-nowrap text-black/[0.64] uppercase", monthly && "[text-box-trim:trim-both] [text-box-edge:cap_alphabetic] max-lg:text-[calc(1.75*var(--page-rem))]")}>
              {data.s5BadgeText}
            </p>
          </div>
        )}
        {(data.s5TitleRegular || data.s5TitleItalic) && (
          <h2 className={cn("mb-0 text-center font-blacker text-[4.5rem] leading-[1.04] font-normal tracking-[-0.135rem] whitespace-nowrap text-black lg:text-[6rem] lg:tracking-[-0.18rem] [&_span]:font-blacker", monthly && "[text-box-trim:trim-both] [text-box-edge:cap_alphabetic] whitespace-normal max-lg:text-[calc(4.25*var(--page-rem))] max-lg:tracking-[calc(-0.1275*var(--page-rem))]")}>
            {data.s5TitleRegular && <span>{data.s5TitleRegular} </span>}
            {data.s5TitleItalic && <span className="text-[#001b14] italic">{data.s5TitleItalic}</span>}
          </h2>
        )}
      </div>
      {!!faqItems.length && <FaqAccordion items={faqItems} initialOpen={initialOpen} layout={layout} />}
    </section>
  );
}
